<template>
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-5 py-5">
    <p class="m-0 text-sm text-muted-foreground">
      {{ tr('Changes to these preferences will apply to future invoices only. If you need a past invoice reissued, please contact hello@infinite.ad.') }}
    </p>

    <form-section>
      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Customer type') }}</ui-label>
        <ui-radio-group v-model="billingType" class="flex gap-6">
          <label class="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <ui-radio-group-item value="company" />
            {{ tr('Company') }}
          </label>
          <label class="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <ui-radio-group-item value="individual" />
            {{ tr('Individual') }}
          </label>
        </ui-radio-group>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label for="billing-name">{{ tr('Billing name') }}<span class="text-destructive">*</span></ui-label>
        <ui-input id="billing-name" v-model="model.billing_company" autocomplete="organization" />
      </div>

      <div v-if="billingType === 'company'" class="flex flex-col gap-1.5">
        <ui-label for="billing-vat">{{ tr('Business tax ID') }}<span class="text-destructive">*</span></ui-label>
        <ui-input id="billing-vat" v-model="model.billing_vat_number" />
        <p class="m-0 text-xs text-muted-foreground">{{ tr('If you are a business tax registrant, please enter your business tax ID here.') }}</p>
      </div>
    </form-section>

    <form-section :title="tr('Primary business address')" :description="primaryBusinessAddressDescription" required>
      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Country') }}<span class="text-destructive">*</span></ui-label>
        <ui-combobox-select
          v-model="model.billing_country"
          :options="countriesList"
          option-value="iso_a2"
          :option-label="(item) => tr(item.name)"
          :placeholder="tr('Choose your country')"
          :search-placeholder="tr('Search') + '...'"
          :empty-text="tr('Sorry, no matching options.')"
        />
      </div>

      <div class="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <div class="flex flex-col gap-1.5">
          <ui-label for="billing-city">{{ tr('City') }}<span class="text-destructive">*</span></ui-label>
          <ui-input id="billing-city" v-model="model.billing_city" autocomplete="address-level2" />
        </div>
        <div class="flex flex-col gap-1.5">
          <ui-label for="billing-zip">{{ tr('Postcode / ZIP') }}<span class="text-destructive">*</span></ui-label>
          <ui-input id="billing-zip" v-model="model.billing_zip" autocomplete="postal-code" />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label for="billing-street">{{ tr('Street address') }}<span class="text-destructive">*</span></ui-label>
        <ui-input id="billing-street" v-model="model.billing_address" autocomplete="street-address" />
      </div>
    </form-section>

    <!-- Lifted above the next glass section so the phone country list is not covered. -->
    <form-section :title="tr('Contact')" class="relative z-10">
      <div class="flex flex-col gap-1.5">
        <ui-label for="billing-email">{{ tr('Billing email') }}<span class="text-destructive">*</span></ui-label>
        <ui-input
          id="billing-email"
          v-model="model.billing_email"
          type="email"
          autocomplete="email"
        />
        <p class="m-0 text-xs text-muted-foreground">{{ tr('Invoices and other billing notifications will be sent here.') }}</p>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Phone number') }}<span class="text-destructive">*</span></ui-label>
        <phone-field ref="telInput" v-model="model.contact_phone" />
        <p class="m-0 text-xs text-muted-foreground">{{ tr('Primary contact number for your company.') }}</p>
      </div>
    </form-section>

    <change-subscription-form
      ref="subscriptionForm"
      :billing-country="model.billing_country"
      :vat-number="billingType === 'company' ? model.billing_vat_number : null"
    />

    <form-section :title="tr('Payment method')">
      <div v-if="currentPaymentMethodIsLoading" class="flex items-center gap-2 text-sm text-muted-foreground">
        <loader-circle-icon class="size-4 animate-spin" />
        {{ tr('Loading...') }}
      </div>

      <template v-else>
        <ui-alert v-if="currentPaymentMethodHasError" variant="destructive">
          <triangle-alert-icon />
          <ui-alert-description>
            {{ tr("We're sorry but currently unable to retrieve information about your payment method.") }}
          </ui-alert-description>
        </ui-alert>

        <div v-else-if="currentPaymentMethod" class="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
          <div class="flex items-center gap-3">
            <img
              v-if="currentCardIconAvailable"
              class="w-8"
              :src="require(`@/assets/img/cards/${currentPaymentMethod.brand}.png`)"
              alt=""
            >
            <credit-card-icon v-else class="size-6 text-muted-foreground" />
            <div class="flex flex-col">
              <span class="text-sm font-semibold text-foreground tabular-nums">•••• {{ currentPaymentMethod.last4 }}</span>
              <span class="text-xs text-muted-foreground">{{ tr('Expires :at', { ':at': currentCardExpireDate }) }}</span>
            </div>
          </div>
          <ui-button
            variant="ghost"
            size="sm"
            class="text-destructive hover:text-destructive"
            @click="() => openDeleteConfirmModal()"
          >
            {{ tr('Remove') }}
          </ui-button>
        </div>

        <p v-else class="m-0 text-sm text-muted-foreground">
          {{ tr('There is no registered payment method.') }}
        </p>

        <template v-if="!currentPaymentMethodHasError">
          <div v-if="showStripeForm" class="flex max-w-sm flex-col gap-3">
            <ui-label>{{ tr('Add payment method') }}</ui-label>
            <stripe-card-input
              ref="stripeForm"
              @success="() => onNewPaymentMethodSaved()"
              @error="(err) => onNewPaymentMethodError(err)"
            />

            <ui-button
              v-if="currentPaymentMethod !== null"
              class="self-start"
              :disabled="isLoading"
              @click="() => save()"
            >
              <loader-circle-icon v-if="isLoading" class="animate-spin" />
              {{ tr('Save payment method') }}
            </ui-button>
          </div>

          <ui-button
            v-else
            variant="outline"
            class="self-start"
            @click="() => showStripeForm = true"
          >
            <credit-card-icon />
            {{ currentPaymentMethod ? tr('Replace payment method') : tr('Add payment method') }}
          </ui-button>
        </template>
      </template>
    </form-section>

    <cancel-subscription-modal-button />
  </div>
</template>

<script>
import { CreditCard, LoaderCircle, TriangleAlert } from '@lucide/vue';

import Company from '@/entities/Company';
import CompanyService from '@/services/Company';
import CompanyPaymentService from '@/services/CompanyPayment';
import CancelSubscriptionModalButton from '@/ui/components/CancelSubscriptionModalButton.vue';
import ChangeSubscriptionForm from '@/ui/components/ChangeSubscriptionForm.vue';
import FormSection from '@/ui/components/FormSection.vue';
import PhoneField from '@/ui/components/PhoneField.vue';
import StripeCardInput from '@/ui/components/StripeCardInput.vue';
import { isValidEmail } from '@/utils/validators';

export default {
  name: 'SettingsBillingAndPaymentView',
  components: {
    CancelSubscriptionModalButton,
    ChangeSubscriptionForm,
    CreditCardIcon: CreditCard,
    FormSection,
    LoaderCircleIcon: LoaderCircle,
    PhoneField,
    StripeCardInput,
    TriangleAlertIcon: TriangleAlert,
  },
  data: () => ({
    model: null,
    isSavingCompany: false,
    isSavingSubscription: false,
    isSavingPaymentMethod: false,

    currentPaymentMethodIsLoading: false,
    currentPaymentMethodHasError: false,
    currentPaymentMethod: null,

    billingType: 'company',

    saveSubscriptionLater: false,
    showStripeForm: false,
    stripeError: null,
  }),
  computed: {
    isLoading() {
      return this.isSavingCompany || this.isSavingSubscription || this.isSavingPaymentMethod;
    },
    companyEntity() {
      return this.$root.currentCompany;
    },
    countriesList() {
      return this.$store.getters['app/countries'];
    },
    currentCardIconAvailable() {
      const availableImages = [
        'amex',
        'diners',
        'discover',
        'jcb',
        'mastercard',
        'visa',
      ];

      return this.currentPaymentMethod && availableImages.includes(this.currentPaymentMethod.brand);
    },
    currentCardExpireDate() {
      if (!this.currentPaymentMethod) {
        return null;
      }

      return this.currentPaymentMethod.exp_month.toString().padStart(2, '0') + ' / ' + this.currentPaymentMethod.exp_year;
    },
    primaryBusinessAddressDescription() {
      return this.tr('This is the physical address of the company purchasing :app service and is used to calculate any applicable sales taxes.');
    },
  },
  watch: {
    currentPaymentMethodIsLoading(val) {
      if (!val && !this.currentPaymentMethod) {
        this.showStripeForm = true;
      }
    },
    isLoading(val) {
      if (!val && this.saveSubscriptionLater) {
        this.saveSubscriptionLater = false;

        if (this.saveCompanyWasSuccessful) {
          this.saveSubscription();
        }
      }
    },
  },
  created() {
    this.model = new Company(this.$root.currentCompany);
    this.billingType = this.model.billing_vat_number || !this.model.billing_company ? 'company' : 'individual';

    this.loadCurrentCard();

    if (!this.model.billing_email) {
      this.model.billing_email = this.$root.loggedInUser.email;
    }
  },
  methods: {
    async save() {
      if (this.isLoading) {
        return false;
      }

      this.$toast.clear();
      this.saveSubscriptionLater = false;

      if (this.billingType === 'individual') {
        this.model.billing_vat_number = '';
      }

      if (!this.validate()) {
        return false;
      }

      if (this.showStripeForm && this.$refs.stripeForm) {
        if (!this.$refs.stripeForm.isCompleted) {
          this.$toast.error(this.tr('You have not specified a payment method.'));
          return false;
        }

        this.saveSubscriptionLater = true;
        this.isSavingPaymentMethod = true;
        const stripeSaveSuccess = await this.$refs.stripeForm.save();
        if (!stripeSaveSuccess) {
          return false;
        }
      }
      else if (!this.currentPaymentMethod) {
        this.$toast.error(this.tr('You have not specified a payment method.'));
        return false;
      }

      this.isSavingCompany = true;
      this.saveCompanyWasSuccessful = false;

      return CompanyService.update(this.$root.currentCompany.id, this.model)
        .then(resp => {
          this.$store.commit('auth/updateCompany', resp);
          return resp;
        })
        .then(async () => {
          this.saveCompanyWasSuccessful = true;

          if (this.$refs.subscriptionForm.isChanged()) {
            if (!this.saveSubscriptionLater) {
              return this.saveSubscription();
            }
          }
          else {
            this.$toast.success(this.tr('Successful save'));
          }

          return new Promise(resolve => resolve());
        })
        .catch(err => {
          this.saveSubscriptionLater = false;
          this.error(err);
        })
        .finally(() => (this.isSavingCompany = false));
    },
    saveSubscription() {
      this.isSavingSubscription = true;

      return this.$refs.subscriptionForm.save()
        .catch(err => this.error(err))
        .finally(() => (this.isSavingSubscription = false));
    },
    validate() {
      if (!this.model.billing_company) {
        this.$toast.error(this.tr('Billing name cannot be empty!'));
        return false;
      }
      if (this.billingType === 'company' && !this.model.billing_vat_number) {
        this.$toast.error(this.tr('Business tax ID cannot be empty!'));
        return false;
      }
      if (!this.model.billing_country) {
        this.$toast.error(this.tr('Country cannot be empty!'));
        return false;
      }
      if (!this.model.billing_city) {
        this.$toast.error(this.tr('City cannot be empty!'));
        return false;
      }
      if (!this.model.billing_zip) {
        this.$toast.error(this.tr('Postcode cannot be empty!'));
        return false;
      }
      if (!this.model.billing_address) {
        this.$toast.error(this.tr('Address cannot be empty!'));
        return false;
      }
      if (!isValidEmail(this.model.billing_email)) {
        this.$toast.error(this.tr('E-mail provided has an incorrect format!'));
        return false;
      }
      if (!this.$refs.telInput.isValid()) {
        this.$toast.error(this.tr('Phone number provided has an incorrect format!'));
        return false;
      }

      /* if (!this.$refs.subscriptionForm.selectedPackage) {
        this.$toast.error(this.tr('You have not selected a subscription package.'));
        return false;
      } */

      return true;
    },

    loadCurrentCard() {
      if (this.currentPaymentMethodIsLoading) {
        return Promise.resolve();
      }

      this.currentPaymentMethodIsLoading = true;
      this.currentPaymentMethod = null;

      return CompanyPaymentService.getStripeCurrentCard(this.$root.currentCompany.id)
        .then(resp => this.currentPaymentMethod = resp)
        .catch(() => this.currentPaymentMethodHasError = true)
        .finally(() => this.currentPaymentMethodIsLoading = false);
    },
    async onNewPaymentMethodSaved() {
      await this.loadCurrentCard();

      this.showStripeForm = false;
      this.isSavingPaymentMethod = false;
    },
    onNewPaymentMethodError(err) {
      this.saveSubscriptionLater = false;
      this.isSavingPaymentMethod = false;

      this.error(err.message ? err.message : err);
    },
    openDeleteConfirmModal() {
      this.$confirmation.open({
        title: this.tr('Remove payment method'),
        content: this.tr('Are you sure you want to remove this payment method?') + '<br>' + this.tr('This action cannot be undone.'),
        labelCancel: this.tr('Cancel'),
        labelConfirm: this.tr('Confirm'),
        destructive: true,
        confirm: () => this.removePaymentMethod(),
      });
    },
    removePaymentMethod() {
      return CompanyPaymentService.deleteStripeCurrentCard(this.$root.currentCompany.id)
        .then(() => this.currentPaymentMethod = null)
        .catch(err => this.error(err));
    },
  },
};
</script>
